import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCertificatePdf, StatusBadge } from "@/lib/certificates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Download, Mail, Printer, Pencil, Loader2, Lock, Save, X, Ban, RotateCcw, AlertTriangle, LifeBuoy, CalendarClock,
} from "lucide-react";

const inputCls =
  "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return s; }
}

function Row({ label, value, locked }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
      <div className="flex items-center gap-1.5">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</p>
        {locked && <Lock className="h-3 w-3 text-neutral-700" />}
      </div>
      <p className="mt-1.5 break-words text-sm text-white">{value || "—"}</p>
    </div>
  );
}

export default function InstallationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const pdf = useCertificatePdf();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});
  const [voidOpen, setVoidOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/installations/${id}`);
      setInst(data);
    } catch (e) {
      toast.error("Certificate not found");
      navigate("/certificates");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setEdit({
      customer_name: inst.customer_name,
      customer_email: inst.customer_email,
      customer_phone: inst.customer_phone,
      vehicle_color: inst.vehicle_color,
      license_plate: inst.license_plate,
      installer_notes: inst.installer_notes,
      installation_package: inst.installation_package || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/installations/${id}`, edit);
      setInst(data);
      setEditing(false);
      toast.success("Installation updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const setE = (k) => (e) => setEdit((s) => ({ ...s, [k]: e.target.value }));

  const doVoid = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setActionBusy(true);
    try {
      const { data } = await api.post(`/installations/${id}/void`, { reason });
      setInst(data);
      setVoidOpen(false); setReason("");
      toast.success("Certificate voided");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Void failed");
    } finally { setActionBusy(false); }
  };

  const doRestore = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setActionBusy(true);
    try {
      const { data } = await api.post(`/installations/${id}/restore-credit`, { reason });
      setInst(data);
      setRestoreOpen(false); setReason("");
      toast.success("1 certificate credit restored");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Restore failed");
    } finally { setActionBusy(false); }
  };

  if (loading || !inst) {
    return (
      <DashboardLayout title="Certificate">
        <div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Certificate Details">
      <button onClick={() => navigate("/certificates")} className="mb-5 inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-white" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to history
      </button>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-mono text-lg font-semibold text-white" data-testid="detail-cert-number">{inst.certificate_number}</p>
            <StatusBadge status={inst.certificate_status} />
          </div>
          <p className="mt-1 text-sm text-neutral-500">{inst.customer_name} · {inst.vehicle_year} {inst.vehicle_make} {inst.vehicle_model}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => pdf.download(inst.id, inst.certificate_number)} disabled={pdf.busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="download-btn">
            <Download className="h-4 w-4" /> Download
          </button>
          <button onClick={() => pdf.email(inst.id)} disabled={pdf.busy} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05] disabled:opacity-60" data-testid="email-btn">
            <Mail className="h-4 w-4" /> Email
          </button>
          <button onClick={() => pdf.print(inst.id)} disabled={pdf.busy} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05] disabled:opacity-60" data-testid="print-btn">
            <Printer className="h-4 w-4" /> Print
          </button>
          {!editing && (
            <button onClick={startEdit} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05]" data-testid="edit-btn">
              <Pencil className="h-4 w-4" /> Edit
            </button>
          )}
          {!isAdmin && inst.certificate_status !== "voided" && (
            <button onClick={() => navigate(`/follow-ups/${inst.id}`)} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05]" data-testid="schedule-reminder-cert-btn">
              <CalendarClock className="h-4 w-4" /> Schedule Reminder
            </button>
          )}
          {isAdmin && inst.certificate_status !== "voided" && (
            <button onClick={() => { setReason(""); setVoidOpen(true); }} className="inline-flex items-center gap-2 rounded-lg border border-[#E30613]/40 px-4 py-2.5 text-sm font-medium text-[#FF6069] transition-colors hover:bg-[#E30613]/10" data-testid="void-btn">
              <Ban className="h-4 w-4" /> Void Certificate
            </button>
          )}
          {isAdmin && inst.certificate_status === "voided" && !inst.credit_restored && (
            <button onClick={() => { setReason(""); setRestoreOpen(true); }} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05]" data-testid="restore-credit-btn">
              <RotateCcw className="h-4 w-4" /> Restore Certificate Credit
            </button>
          )}
        </div>
      </div>

      {inst.certificate_status === "voided" && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#E30613]/30 bg-[#E30613]/10 px-5 py-4" data-testid="voided-banner">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-[#FF6069]" />
          <div>
            <p className="text-sm font-semibold text-[#FF6069]">This certificate has been VOIDED</p>
            <p className="mt-1 text-xs text-neutral-400">
              {inst.void_reason ? `Reason: ${inst.void_reason}. ` : ""}
              {inst.credit_restored ? "Credit Restored." : "Credit Not Restored."}
            </p>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-white/[0.06] bg-[#141414] px-5 py-4" data-testid="installer-void-note">
          <LifeBuoy className="mt-0.5 h-5 w-5 text-[#FF1C2A]" />
          <div>
            <p className="text-sm font-medium text-white">Contact SGBINOXE to Void Certificate</p>
            <p className="mt-1 text-xs text-neutral-400">If this certificate was created by mistake, please contact SGBINOXE Support. Only a SGBINOXE administrator can void a certificate or restore a certificate credit.</p>
          </div>
        </div>
      )}

      {editing ? (
        <div className="max-w-4xl space-y-6" data-testid="edit-form">
          <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6 md:p-8">
            <h3 className="mb-6 font-display text-base font-semibold uppercase tracking-wider text-white">Edit Installation</h3>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-2"><Label className="text-neutral-300">Customer Name</Label><Input value={edit.customer_name} onChange={setE("customer_name")} className={inputCls} data-testid="edit-customer-name" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Customer Email</Label><Input value={edit.customer_email} onChange={setE("customer_email")} className={inputCls} data-testid="edit-customer-email" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Customer Phone</Label><Input value={edit.customer_phone} onChange={setE("customer_phone")} className={inputCls} data-testid="edit-customer-phone" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Vehicle Color</Label><Input value={edit.vehicle_color} onChange={setE("vehicle_color")} className={inputCls} data-testid="edit-vehicle-color" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">License Plate</Label><Input value={edit.license_plate} onChange={setE("license_plate")} className={inputCls} data-testid="edit-license-plate" /></div>
              {isAdmin && (
                <div className="space-y-2"><Label className="text-neutral-300">Installation Package (admin)</Label>
                  <Select value={edit.installation_package} onValueChange={(v) => setEdit((s) => ({ ...s, installation_package: v }))}>
                    <SelectTrigger className={inputCls} data-testid="edit-package"><SelectValue placeholder="Not Specified" /></SelectTrigger>
                    <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                      <SelectItem value="Essential">Essential</SelectItem>
                      <SelectItem value="Complete">Complete</SelectItem>
                      <SelectItem value="Ultimate">Ultimate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2 md:col-span-2"><Label className="text-neutral-300">Installer Notes</Label>
                <Textarea value={edit.installer_notes} onChange={setE("installer_notes")} rows={4} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]" data-testid="edit-notes" />
              </div>
            </div>
            <p className="mt-5 flex items-center gap-1.5 text-xs text-neutral-500">
              <Lock className="h-3 w-3" /> Certificate number, VIN, installation date, installer and product require administrator permission to change.
            </p>
            <div className="mt-6 flex gap-3">
              <button onClick={saveEdit} disabled={saving} className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#E30613] px-6 text-sm font-medium text-white transition-colors hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="save-edit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
              </button>
              <button onClick={() => setEditing(false)} className="inline-flex h-11 items-center gap-2 rounded-lg border border-white/[0.1] px-5 text-sm font-medium text-neutral-300 transition-colors hover:bg-white/[0.05]">
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-5xl space-y-6">
          <div>
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Customer</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Row label="Customer Name" value={inst.customer_name} />
              <Row label="Customer Email" value={inst.customer_email} />
              <Row label="Customer Phone" value={inst.customer_phone} />
            </div>
          </div>
          <div>
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Vehicle</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Row label="VIN" value={inst.vin} locked />
              <Row label="Year" value={inst.vehicle_year} locked />
              <Row label="Make" value={inst.vehicle_make} locked />
              <Row label="Model" value={inst.vehicle_model} locked />
              <Row label="Color" value={inst.vehicle_color} />
              <Row label="License Plate" value={inst.license_plate} />
            </div>
          </div>
          <div>
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Installation</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Row label="Product Installed" value={inst.product_installed} locked />
              <Row label="Installation Package" value={inst.installation_package || "Not Specified"} locked />
              <Row label="Installation Date" value={fmtDate(inst.installation_date)} locked />
              <Row label="Follow Up Date" value={fmtDate(inst.follow_up_date)} />
              <Row label="Installer Business" value={inst.installer_business} locked />
              <Row label="Installer Contact" value={inst.installer_contact} locked />
              <Row label="Issue Date" value={fmtDate(inst.created_at)} />
            </div>
            {inst.installer_notes && (
              <div className="mt-4 rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">Installer Notes</p>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-neutral-300">{inst.installer_notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={voidOpen} onOpenChange={(o) => !actionBusy && setVoidOpen(o)}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="void-modal">
          <DialogHeader><DialogTitle>Void Certificate</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-400">This action will mark the certificate as voided. The certificate will remain in the system for audit purposes. A certificate credit will not be restored automatically.</p>
          <div className="space-y-2">
            <Label className="text-neutral-300">Reason for voiding</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="void-reason" />
          </div>
          <DialogFooter>
            <button onClick={() => setVoidOpen(false)} disabled={actionBusy} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={doVoid} disabled={actionBusy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-void">{actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Void Certificate"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={restoreOpen} onOpenChange={(o) => !actionBusy && setRestoreOpen(o)}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="restore-modal">
          <DialogHeader><DialogTitle>Restore Certificate Credit</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-400">Restore 1 certificate credit to this installer?</p>
          <div className="space-y-2">
            <Label className="text-neutral-300">Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="restore-reason" />
          </div>
          <DialogFooter>
            <button onClick={() => setRestoreOpen(false)} disabled={actionBusy} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={doRestore} disabled={actionBusy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-restore">{actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restore Credit"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
